import React, { useState, useEffect, useRef } from 'react';
import {
  Select,
  MenuItem,
  Checkbox,
  Autocomplete,
  Chip,
  Box,
  CircularProgress,
} from '@mui/material';
import { AppTextField } from '../../design-system';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { TableColumn, RowData, CellValue } from './types';

interface EditableCellProps<T = RowData> {
  row: T;
  column: TableColumn<T>;
  value: CellValue;
  onSave: (newValue: CellValue) => Promise<void>;
  onCancel: () => void;
}

function EditableCell<T extends object = RowData>(props: EditableCellProps<T>) {
  const { row, column, value, onSave, onCancel } = props;
  const [currentValue, setCurrentValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSave = async () => {
    // Validate if needed
    if (column.editOptions?.validate) {
      const error = column.editOptions.validate(currentValue, row);
      if (error) {
        alert(error); // TODO: Better error display
        return;
      }
    }

    // Check if value changed
    if (currentValue === value) {
      onCancel();
      return;
    }

    setSaving(true);
    try {
      await onSave(currentValue);
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save changes'); // TODO: Better error display
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  // Custom edit cell renderer
  if (column.editType === 'custom' && column.editOptions?.renderEditCell) {
    return (
      <>
        {column.editOptions.renderEditCell({
          row,
          field: String(column.field),
          value: currentValue,
          onSave: handleSave,
          onCancel,
        })}
      </>
    );
  }

  // Text input
  if (column.editType === 'text' || column.editType === 'number' || !column.editType) {
    return (
      <AppTextField
        inputRef={inputRef}
        type={column.editType === 'number' ? 'number' : 'text'}
        value={currentValue || ''}
        onChange={(e) => setCurrentValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        size="small"
        fullWidth
        autoFocus
        disabled={saving}
        InputProps={{
          endAdornment: saving && <CircularProgress size={16} />,
        }}
      />
    );
  }

  // Select dropdown
  if (column.editType === 'select') {
    return (
      <Select
        value={currentValue || ''}
        onChange={(e) => {
          setCurrentValue(e.target.value);
          // Auto-save on selection
          setTimeout(() => {
            onSave(e.target.value);
          }, 0);
        }}
        onKeyDown={handleKeyDown}
        size="small"
        fullWidth
        autoFocus
        disabled={saving}
      >
        {column.editOptions?.options?.map((option) => (
          <MenuItem key={String(option.value)} value={String(option.value ?? '')} disabled={option.disabled}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    );
  }

  // Autocomplete (single or multiple)
  if (column.editType === 'autocomplete') {
    const options = column.editOptions?.options || [];
    const multiple = column.editOptions?.multiple || false;

    return (
      <Autocomplete
        multiple={multiple}
        value={
          multiple
            ? options.filter((opt) => Array.isArray(currentValue) && currentValue.includes(opt.value))
            : options.find((opt) => opt.value === currentValue) || null
        }
        onChange={(_, newValue) => {
          const newVal = multiple
            ? (newValue as typeof options).map((v) => v.value)
            : (newValue as typeof options[0])?.value || null;
          setCurrentValue(newVal);
        }}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        options={options}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={String(option.value)}
              label={option.label}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <AppTextField
            {...params}
            inputRef={inputRef}
            size="small"
            autoFocus
            placeholder={multiple ? 'Select...' : ''}
          />
        )}
        size="small"
        fullWidth
        autoFocus
        disabled={saving}
        sx={{
          '& .MuiAutocomplete-inputRoot': {
            minHeight: 40,
          },
        }}
      />
    );
  }

  // Date picker
  if (column.editType === 'date') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          value={currentValue ? new Date(String(currentValue)) : null}
          onChange={(newValue) => {
            setCurrentValue(newValue);
            // Auto-save on selection
            setTimeout(() => {
              onSave(newValue);
            }, 0);
          }}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              autoFocus: true,
              onKeyDown: handleKeyDown,
            },
          }}
          disabled={saving}
        />
      </LocalizationProvider>
    );
  }

  // Checkbox
  if (column.editType === 'checkbox') {
    return (
      <Checkbox
        checked={Boolean(currentValue)}
        onChange={(e) => {
          const newValue = e.target.checked;
          setCurrentValue(newValue);
          // Auto-save on toggle
          setTimeout(() => {
            onSave(newValue);
          }, 0);
        }}
        disabled={saving}
      />
    );
  }

  // Fallback
  return <Box>{String(value)}</Box>;
}

export default EditableCell;
